/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState, type ReactNode } from 'react'

import { Alert, AssetDetailModal, SimpleTable, TableSkeleton } from '@/components/ui'
import { useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { formatDistance } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatMinutes } from '@/utils/formatDate'

export interface TripReeferSummaryParams extends InsightParametersType {
  companyId?: number
  assetIds?: string[]
  date?: string
  from?: string
  to?: string
}

export interface TripReeferSummaryRecord {
  assetId: number
  assetName: string
  deviceName: string
  totalDistanceKm: number | null
  totalReeferOnMinutes: number | null
}

export interface TripReeferSummaryResponse {
  data: TripReeferSummaryRecord[]
  count: number
}

export type TripReeferSummaryData = TripReeferSummaryRecord[] & {
  count: number
}

export const reportPath = '/insights/reports/trip-reefer-summary'

const HEADERS = ['Asset', 'Device Name', 'Total Distance Traveled', 'Total Reefer On Time']

const TripReeferSummary: InsightDefinition<TripReeferSummaryParams, TripReeferSummaryData> = {
  active: true,
  slug: 'report/trip-reefer-summary',
  category: 'report',
  name: 'Distance Traveled Reefer Power Summary by Asset',
  description: 'Summarize distance traveled and reefer power-on time for selected assets.',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'companyId') {
        return { ...parameters, assetIds: undefined }
      }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'assetIds',
        control: 'AssetDropdownMulti',
        required: true,
        includeSelectAll: true,
        assetType: AssetTypeFilter.InTransit,
      },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
      {
        id: 'dateRange',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (parameters: TripReeferSummaryParams) => {
      if (parameters.date === 'custom') {
        if (!parameters.from || !parameters.to) {
          return 'Enter the custom date range.'
        }

        const fromDate = new Date(parameters.from)
        const toDate = new Date(parameters.to)
        if (fromDate > toDate) {
          return 'Start date must be before end date'
        }

        const dateValidation = validateDateRangeWithin13Months(parameters.from, parameters.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }

      if (parameters.companyId && parameters.assetIds?.length) {
        return true
      }

      return 'Company and at least one Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: TripReeferSummaryParams) => {
    const response = await api.get<TripReeferSummaryRecord[]>({
      path: `${reportPath}?${buildQuery(parameters)}&take=1000`,
      origin: 'TripReeferSummary',
    })

    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }

    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }

    return Object.assign([...response.data], { count: response.count ?? 0 })
  },

  csv: async (api: ApiService, parameters: TripReeferSummaryParams, title: string) => {
    await downloadCsv(api, reportPath, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const parameters = configuration.parameters as TripReeferSummaryParams
    const units = (useDashboard().configuration.units as 'Imperial' | 'Metric') ?? 'Metric'

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={HEADERS.length} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data) || !data?.length) {
      return null
    }

    const body: Record<string, ReactNode>[] = data.map((row) => ({
      Asset: (
        <button
          type="button"
          onClick={() => setSelectedAsset(row.assetId)}
          className="text-left block text-blue-500 cursor-pointer"
        >
          {row.assetName}
        </button>
      ),
      'Device Name': row.deviceName,
      'Total Distance Traveled': formatDistance(row.totalDistanceKm, units),
      'Total Reefer On Time': formatMinutes(row.totalReeferOnMinutes),
    }))

    const totalDistance = data.reduce((sum, row) => sum + Number(formatDistance(row.totalDistanceKm, units)), 0)
    const totalReeferOnTime = data.reduce((sum, row) => sum + Number(formatMinutes(row.totalReeferOnMinutes)), 0)

    body.push({
      Asset: '',
      'Device Name': <strong>Totals</strong>,
      'Total Distance Traveled': totalDistance.toFixed(2),
      'Total Reefer On Time': totalReeferOnTime.toFixed(2),
    })

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={HEADERS} body={body} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    const parameters = configuration.parameters as TripReeferSummaryParams

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }

    const fields: Record<string, string> = {}
    const company = companies.find((item) => item.id === parameters.companyId)
    if (company) fields.Company = company.name
    if (parameters.assetIds?.length) {
      fields.Assets = `${parameters.assetIds.length} Asset${parameters.assetIds.length === 1 ? '' : 's'}`
    }
    addDateToCaptionFields({ ...parameters, fields, finite: true })

    return Object.keys(fields).length ? <Caption fields={fields} /> : ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA || !data?.length) {
      return <div>No records found</div>
    }

    const rows = data.count ?? data.length
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default TripReeferSummary
